import {
  asArray,
  asBool,
  asNumber,
  asRecord,
  asString,
  assertArray,
} from "@/data/mapper/common/mapper-utils";
import { PrescriptionInfoMapper } from "@/data/mapper/prescription/prescription-info-mapper";
import { TreatmentSummaryMapper } from "@/data/mapper/treatment/treatment-summary-mapper";
import { PatientDetail, PatientSummary } from "@/domain/entity/patient/patient-info-entity";

export class PatientInfoMapper {
  static anonymizeName(name: string | null | undefined): string {
    if (!name) return "-";
    return name.replace(/./g, "#");
  }

  static toEntity(dto: unknown): PatientSummary {
    const map = asRecord(dto);
    const doctor = asRecord(map.doctor);
    const medicalExpert = asRecord(map.medicalExpert);
    const uuid = asString(map.patientUuid ?? map.uuid ?? map.userUuid);
    const rawName = asString(
      map.patientName ??
        map.userName ??
        map.fullName ??
        map.patientFullName ??
        map.name,
      "-",
    );
    const doctorUuid = firstString(
      map.doctorUuid,
      map.medicalExpertUuid,
      map.medicalExpertUserUuid,
      doctor.uuid,
      doctor.doctorUuid,
      doctor.userUuid,
      medicalExpert.uuid,
      medicalExpert.doctorUuid,
      medicalExpert.userUuid,
    );
    return {
      doctorId: map.doctorId == null ? null : asNumber(map.doctorId),
      ...(doctorUuid == null ? {} : { doctorUuid }),
      doctorName:
        map.doctorName == null &&
        map.medicalExpertName == null &&
        doctor.name == null &&
        medicalExpert.name == null
          ? null
          : asString(map.doctorName ?? map.medicalExpertName ?? doctor.name ?? medicalExpert.name),
      uuid,
      timeZone: map.patientTimeZone == null ? null : asString(map.patientTimeZone),
      progressStatus:
        map.progressStatus == null &&
        map.treatmentStatus == null &&
        map.treatmentState == null &&
        map.prescriptionStatus == null &&
        map.status == null
          ? null
          : asString(
              map.progressStatus ??
                map.treatmentStatus ??
                map.treatmentState ??
                map.prescriptionStatus ??
                map.status,
            ),
      latestComplianceRate:
        map.latestComplianceRate == null && map.complianceRate == null
          ? null
          : toPercentValue(map.latestComplianceRate ?? map.complianceRate),
      patientEmail:
        map.patientEmail == null && map.email == null && map.loginId == null
          ? null
          : asString(map.patientEmail ?? map.email ?? map.loginId),
      patientName: PatientInfoMapper.anonymizeName(rawName),
      patientSearchName: rawName,
      isDeactivated: asBool(map.isDeactivated ?? map.deactivated ?? map.isDeleted),
      registrationDate:
        map.registrationDate == null && map.createdAt == null
          ? null
          : asString(map.registrationDate ?? map.createdAt),
      isTestAccount: asBool(map.isTestAccount),
      screeningId:
        map.screeningId == null &&
        map.screeningID == null &&
        map.screening_id == null &&
        map.patientScreeningId == null
          ? null
          : asString(
              map.screeningId ??
                map.screeningID ??
                map.screening_id ??
                map.patientScreeningId,
            ),
      phone:
        map.phone == null && map.patientPhone == null
          ? null
          : asString(map.phone ?? map.patientPhone),
      gender:
        map.gender == null && map.patientGender == null
          ? null
          : asString(map.gender ?? map.patientGender),
      birthday: readPatientBirthDate(map),
      isAccountSetup:
        map.isAccountSetup == null && map.accountSetup == null
          ? null
          : asBool(map.isAccountSetup ?? map.accountSetup),
      canAddTreatment: typeof map.canAddTreatment === "boolean" ? map.canAddTreatment : null,
      treatments: patientTreatmentsFromMap(map),
    };
  }

  static toEntityList(dto: unknown): PatientSummary[] {
    return assertArray(dto, "PatientInfoMapper::toEntityList").map((item) =>
      PatientInfoMapper.toEntity(item),
    );
  }

  static toDetailEntity(dto: unknown): PatientDetail {
    const map = asRecord(dto);
    const detail = asRecord(map.detail);
    const rawPrescriptions =
      map.prescriptions ??
      map.prescriptionList ??
      map.treatmentList ??
      map.treatments;
    return {
      ...PatientInfoMapper.toEntity(map),
      phone: asString(map.phone ?? map.patientPhone ?? detail.phone, ""),
      countryCode: asString(map.countryCode ?? detail.countryCode, "kr"),
      gender: asString(detail.gender ?? map.gender ?? map.patientGender, ""),
      birthday: readPatientBirthDate(map) ?? "",
      memo:
        detail.memo == null && map.memo == null && map.patientMemo == null
          ? null
          : asString(detail.memo ?? map.memo ?? map.patientMemo),
      prescriptions:
        rawPrescriptions == null
          ? undefined
          : asArray(rawPrescriptions).map((item) => PrescriptionInfoMapper.toSummaryEntity(item)),
    };
  }
}

function readPatientBirthDate(map: Record<string, unknown>): string | null {
  const patient = asRecord(map.patient);
  const user = asRecord(map.user);
  const detail = asRecord(map.detail);
  const value =
    detail.birthDate ??
    detail.birthday ??
    detail.patientBirthday ??
    map.birthDate ??
    map.birthday ??
    map.patientBirthday ??
    map.dateOfBirth ??
    map.dob ??
    patient.birthDate ??
    patient.birthday ??
    patient.patientBirthday ??
    user.birthDate ??
    user.birthday ??
    user.patientBirthday;
  return value == null ? null : asString(value);
}

function patientTreatmentsFromMap(map: Record<string, unknown>) {
  const list =
    map.treatmentList ??
    map.treatments ??
    map.prescriptionList ??
    map.prescriptions;
  return list == null ? undefined : asArray(list).map((item) => TreatmentSummaryMapper.toEntity(item));
}

function toPercentValue(value: unknown): number {
  const numberValue = asNumber(value);
  return numberValue > 0 && numberValue <= 1 ? Math.round(numberValue * 100) : numberValue;
}

function firstString(...values: unknown[]): string | null {
  for (const value of values) {
    if (value == null || typeof value === "object") continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return null;
}
